import React, { useState, useEffect } from 'react'
import { CircularProgress } from '@mui/material'
import {
    formatAddress,
    formatCurrency,
    formatDate,
    formatSessionState,
} from '../../../../utilities/generalUtils'


const CardIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
        <line x1="6" y1="15" x2="10" y2="15" />
    </svg>
)

function getChargingSpeed(session) {
    const watts = session?.powerDraw?.watts ?? 0
    const kilowatts = watts / 1000
    const formattedKw =
        kilowatts >= 1 ? kilowatts.toFixed(1) : kilowatts.toFixed(2)
    return `${formattedKw} kW AC`
}

function getPowerConsumed(session) {
    const power = session?.powerConsumedInKwh?.power ?? '-'
    const unit = session?.powerConsumedInKwh?.unit ?? ''
    return `${power} ${unit}`.trim()
}

// "$0.00" → ["0", "00"]
function splitCurrency(value) {
    const formatted = formatCurrency(value || 0) // e.g. "$0.00"
    const m = String(formatted).match(/(\D*)([\d,]+)(?:\.(\d+))?/)
    if (!m) return { sym: '$', whole: '0', frac: '00' }
    return { sym: m[1] || '$', whole: m[2] || '0', frac: m[3] || '00' }
}

const ActiveSessionCard = ({
    activeSession,
    stoppedSessionData,
    sessionStoppedAwaitingComplete,
    sessionCompletedDisplay,
    showConfirmStop,
    myProperty,
    onStopClick,
    onConfirmStop,
    onCancelStop,
    onDismiss,
}) => {
    const stoppedView =
        sessionStoppedAwaitingComplete || sessionCompletedDisplay
    const session = (stoppedView ? stoppedSessionData : activeSession) ||
        activeSession
    const sessionInner = session?.session

    const [showCollecting, setShowCollecting] = useState(false)
    useEffect(() => {
        if (!sessionStoppedAwaitingComplete) return
        setShowCollecting(false)
        const t = setTimeout(() => setShowCollecting(true), 2000)
        return () => clearTimeout(t)
    }, [sessionStoppedAwaitingComplete])

    if (!sessionInner) return null

    const cost = splitCurrency(sessionInner.chargingCost)
    const startedAt = formatDate(sessionInner.startedAt)
    const [startDate, startTime] = String(startedAt).includes(',')
        ? startedAt.split(/,\s*/)
        : [startedAt, '']
    const address = formatAddress(myProperty) || ''

    return (
        <div className="gc-card">
            {!stoppedView && (
                <div className="gc-active-banner" role="status">
                    <span className="gc-pulse-dot" aria-hidden="true" />
                    Charging in progress
                </div>
            )}

            <h1 className="gc-page-title">Guest charging</h1>

            <div className="gc-cost-hero">
                <div className="gc-cost-hero-label">Current cost</div>
                <div
                    className="gc-cost-hero-value"
                    aria-label={`Current cost ${cost.sym}${cost.whole}.${cost.frac}`}
                >
                    <span className="s">{cost.sym}</span>
                    {cost.whole}
                    <span className="c">.{cost.frac}</span>
                </div>
            </div>

            <div className="gc-stat-grid">
                <div>
                    <div className="gc-stat-label">Property</div>
                    <div className="gc-stat-value">{address}</div>
                </div>
                <div>
                    <div className="gc-stat-label">Started at</div>
                    <div className="gc-stat-value mono">
                        {startDate}
                        {startTime && (
                            <>
                                <br />
                                {startTime}
                            </>
                        )}
                    </div>
                </div>
                <div>
                    <div className="gc-stat-label">Charging speed</div>
                    <div className="gc-stat-value mono">{getChargingSpeed(sessionInner)}</div>
                </div>
                <div>
                    <div className="gc-stat-label">Energy consumed</div>
                    <div className="gc-stat-value mono">{getPowerConsumed(sessionInner)}</div>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                    <div className="gc-stat-label">Charging state</div>
                    <div className="gc-stat-state">
                        <span className="dot" />
                        {formatSessionState(sessionInner.state) || 'Active'}
                    </div>
                </div>
            </div>

            {stoppedView && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 10,
                            margin: '6px 0 14px',
                            font: 'var(--gp-body-med)',
                            color: 'var(--gp-ink)',
                        }}
                    >
                        {!sessionCompletedDisplay && (
                            <CircularProgress size={18} style={{ color: 'var(--gp-brand)' }} />
                        )}
                        {showCollecting ? 'Collecting final amount…' : 'Stopping charge…'}
                    </div>
                    <div className="gc-stack">
                        <button
                            type="button"
                            className="gc-btn gc-btn-primary"
                            onClick={onDismiss}
                        >
                            Dismiss
                        </button>
                    </div>
                </>
            )}

            {!stoppedView && showConfirmStop && (
                <>
                    <p className="gc-confirm-text">Are you sure you'd like to stop charging?</p>
                    <div className="gc-confirm-row">
                        <button
                            type="button"
                            className="gc-btn gc-btn-secondary"
                            onClick={onCancelStop}
                        >
                            No
                        </button>
                        <button
                            type="button"
                            className="gc-btn gc-btn-danger"
                            onClick={onConfirmStop}
                        >
                            Yes
                        </button>
                    </div>
                </>
            )}

            {!stoppedView && !showConfirmStop && (
                <div className="gc-stack">
                    <button
                        type="button"
                        className="gc-btn gc-btn-danger"
                        onClick={onStopClick}
                        aria-label="Stop charging session"
                    >
                        Stop Charging
                    </button>
                </div>
            )}
        </div>
    )
}

export default ActiveSessionCard
